/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';

// Component imports
import { Button, Col, Divider, Empty, Form, Input, Popover, Progress, Radio, Row, Space, Switch, Table, Tooltip, Typography } from 'antd';
import { ExclamationCircleOutlined, MinusCircleOutlined, MinusOutlined, PlusCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { CheckCircleIcon } from '../Icons/CheckCircleIcon';
import { CloseCircleIcon } from '../Icons/CloseCircleIcon';
import { ArrowDownIcon } from '../Icons/ArrowDownIcon';
import BotAvatar from '../UI/BotAvatar';
import ObservatoryFiltersRow, { useInitialTenantFilters, useObservatoryFilters, useObservatorySearchParams } from '../UI/ObservatoryFiltersRow';

// Misc imports
import { MONITORING_STATUSES, DEFAULT_PAGINATION_SIZE, DATETIME_BACKEND_FORMAT } from '../../constants/main';
import { useCreateBotMonitoringMutation, useDeleteBotMonitoringMutation, useFetchMonitorsQuery } from '../../store/monitoring/monitoringApi';
import { useFetchBotsQuery, useUpdateBotMutation } from '../../store/bots/botsApi';
import { useFetchOrganizationQuery } from '../../store/organization/organizationApi';
import { useFetchUsersThinListQuery } from '../../store/users/usersApi';

const MonitoringTable = () => {
  const [filtersForm] = Form.useForm();
  const [alertEmailsForm] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { orgId } = useParams();
  const [searchParams] = useSearchParams();

  const [pageSize, setPageSize] = useState(DEFAULT_PAGINATION_SIZE);
  const [currentPage, setCurrentPage] = useState(1);

  const { data: organization } = useFetchOrganizationQuery({ orgId }, { skip: !orgId });
  const orgTimezone = organization?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const observatorySearchParams = useObservatorySearchParams(orgTimezone);
  const observatoryFilters = useObservatoryFilters(orgTimezone);
  const initialTenantFilters = useInitialTenantFilters();

  const { data: bots, isFetching: isFetchingBots } = useFetchBotsQuery({ orgId, urlParams: { includeBotDeployments: true } }, { skip: !orgId });
  const { data: usersThinList } = useFetchUsersThinListQuery({ orgId }, { skip: !orgId });
  const [updateBot, { isLoading: isUpdatingBot }] = useUpdateBotMutation();

  const endOfWeek = observatoryFilters?.endDate ? moment(observatoryFilters.endDate).endOf('week').format(DATETIME_BACKEND_FORMAT) : moment().endOf('week').format(DATETIME_BACKEND_FORMAT);

  const { data: monitoring, isFetching: isFetchingMonitoring, isError: fetchMonitoringError, refetch: fetchMonitoring } = useFetchMonitorsQuery({
    orgId,
    urlParams: {
      tenantsFilter: observatorySearchParams.tenantsFilter,
      endDate: `${endOfWeek}${encodeURIComponent(moment().tz(orgTimezone)?.format('Z'))}` || '',
      page: searchParams.get('page') || currentPage - 1,
      pageSize: searchParams.get('pageSize') || pageSize,
      order: searchParams.get('order') || 'asc',
      sortBy: searchParams.get('sortBy') || 'displayName'
    }
  }, { skip: !organization });

  const [createBotMonitoring, { isLoading: isCreatingBotMonitoring }] = useCreateBotMonitoringMutation();
  const [deleteBotMonitoring, { isLoading: isDeletingBotMonitoring }] = useDeleteBotMonitoringMutation();

  const [openPopoverId, setOpenPopoverId] = useState(null);
  const [, updateState] = useState(); // Hook to force a component re-render
  const forceUpdate = React.useCallback(() => updateState({}), []);

  const filteredUserEmails = [...new Set((usersThinList?.map(user => user?.email) ?? []))].filter(email => email.toLowerCase().includes(alertEmailsForm.getFieldValue('emailFilter')?.toLowerCase() || ''));

  const columns = [
    {
      title: t('Agent Name'),
      key: 'displayName',
      sorter: true,
      render: record => <BotAvatar bot={record.tenant} wrapBotName />
    },
    // Array of 7 items for each weekday, calculating backward from endDate
    ...[...Array(7).keys()].map((weekday, index) => {
      // Calculate days by going backwards from the end date
      // This gives us days from Monday to Sunday, with Sunday being the end date
      const dayOffset = 6 - weekday; // 6 days before end = Monday, 0 days before end = Sunday
      return ({
        title: moment(endOfWeek).subtract(dayOffset, 'days').format('ddd DD/MM'),
        key: 'ratio' + index,
        align: 'center',
        responsive: ['md'],
        render: record => {
          const ratio = record.weeklyStatus?.[index]?.ratio;
          if (ratio > 99) {
            return (
              <Tooltip overlayClassName="pointer-events-none" title={`${t('Uptime')}: ${ratio}%`} placement="left">
                <CheckCircleIcon className="fs-xl text-success"/>
              </Tooltip>
            );
          } else if (ratio > 0) {
            return (
              <Tooltip overlayClassName="pointer-events-none" title={`${t('Uptime')}: ${ratio}%`} placement="left">
                <ExclamationCircleOutlined className="fs-xl text-warning"/>
              </Tooltip>
            );
          } else {
            return (
              <Tooltip overlayClassName="pointer-events-none" title={ratio ? `${t('Uptime')}: ${ratio}%` : t('No uptime')} placement="left">
                <MinusOutlined className="text-muted"/>
              </Tooltip>
            );
          }
        }
      });
    }),
    {
      title: t('Current Status'),
      key: 'currentStatus',
      align: 'center',
      render: record => renderCurrentMonitoringStatus(record)
    },
    {
      title: t('Enable Agent Monitoring'),
      key: 'actions',
      align: 'center',
      render: record => (
        <Space>
          <Switch
            loading={isCreatingBotMonitoring || isDeletingBotMonitoring}
            checked={!!record.tenant?.settings?.monitoring?.enabled}
            onChange={async (checked) => {
              if (checked) {
                createBotMonitoring({ orgId, tenantId: record.tenant.id });
              } else {
                deleteBotMonitoring({ orgId, tenantId: record.tenant.id });
              }
            }}
          />

          <Popover
            open={openPopoverId === record.tenant?.id}
            overlayStyle={{ maxWidth: 300 }}
            placement="bottomLeft"
            overlayClassName="p-0"
            trigger="click"
            onOpenChange={open => {
              if (open) {
                setOpenPopoverId(record.tenant?.id);
                alertEmailsForm.setFieldValue('alertEmail', record.tenant?.settings?.monitoring?.alertEmail);
              } else {
                setOpenPopoverId(null);
              }
            }}
            afterClose={() => alertEmailsForm.resetFields()}
            content={
              <Form
                name="alertEmailsForm"
                form={alertEmailsForm}
                layout="vertical"
                onFieldsChange={(_, allFields) => {
                  forceUpdate(allFields);
                }}
                onFinish={async (values) => {
                  await updateBot({ orgId, tenantId: record.tenant?.id,
                    data: {
                      settings: {
                        monitoring: {
                          alertEmail: values.alertEmail
                        }
                      }
                    } }
                  );

                  fetchMonitoring();
                }}
              >
                <Form.Item
                  name="emailFilter"
                  className="mb-2 px-2 pt-2"
                  label={t('Send Email Notifications to the following Console Users:')}
                >
                  <Input
                    size="large"
                    placeholder={t('Search users')}
                    prefix={<SearchOutlined className="text-secondary" />}
                    allowClear
                  />
                </Form.Item>

                <Divider className="my-0" />

                <Form.Item
                  name="alertEmail"
                  className="mb-0"
                >
                  <Radio.Group
                    className="px-4 py-2 d-flex flex-column overflow-y-auto overflow-x-hidden scrollbar-custom"
                    style={{ maxHeight: 140 }}
                    options={
                      filteredUserEmails
                        .sort((a, b) => a.localeCompare(b))
                        .map(email => (
                          {
                            label: <Typography.Text ellipsis>{email}</Typography.Text>,
                            value: email,
                            style: { marginBottom: 4, marginTop: 4 }
                          }
                        ))
                    }
                  />
                </Form.Item>

                {
                  filteredUserEmails.length === 0 ? (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} className="mt-3 mb-7" />
                  ) : null
                }

                <Divider className="my-0" />

                <Row gutter={8} justify="end" className="p-2">
                  <Col>
                    <Button
                      disabled={isFetchingMonitoring || isUpdatingBot}
                      size="large"
                      onClick={() => {
                        alertEmailsForm.setFieldValue('alertEmail', null);
                      }}
                    >
                      {t('Clear')}
                    </Button>
                  </Col>

                  <Col>
                    <Button
                      disabled={isFetchingMonitoring || isUpdatingBot}
                      type="primary"
                      size="large"
                      htmlType="submit"
                      onClick={() => {
                        setOpenPopoverId(null);
                      }}
                    >
                      {t('OK')}
                    </Button>
                  </Col>
                </Row>
              </Form>
            }
          >
            <Button
              className="text-secondary bg-layout mix-blend-mode-multiply"
              type="text"
              shape="circle"
              size="small"
              icon={<ArrowDownIcon className="fs-sm" />}
            />
          </Popover>
        </Space>
      )
    }
  ];

  // Memoized values
  const sortingInfo = useMemo(() => {
    let order = searchParams.get('order') ? (searchParams.get('order') === 'desc' ? 'descend' : 'ascend') : 'ascend';
    let columnKey = searchParams.get('sortBy') ? searchParams.get('sortBy') : 'displayName';

    return {
      order,
      columnKey
    };
  }, [searchParams.get('order'), searchParams.get('sortBy')]);

  // On mount
  useEffect(() => {
    updateUrlSearchParams(Number(searchParams.get('page')) + 1, DEFAULT_PAGINATION_SIZE, { [sortingInfo.columnKey]: sortingInfo.order === 'descend' ? 'desc' : 'asc' });
  }, [orgTimezone]);

  const updateUrlSearchParams = (current = 1, pageSize, sorting = { displayName: 'asc' }) => {
    setCurrentPage(current);
    if (pageSize) {
      setPageSize(pageSize);
    }
    const urlSearchParams = new URLSearchParams(location.search);

    urlSearchParams.set('page', current - 1);

    if (pageSize) {
      urlSearchParams.set('pageSize', pageSize);
    }

    if (sorting) {
      urlSearchParams.set('sortBy', Object.entries(sorting).flat()[0]);
      urlSearchParams.set('order', Object.entries(sorting).flat()[1]);
    } else {
      urlSearchParams.delete('sortBy');
      urlSearchParams.delete('order');
    }

    // Set the filters to the url
    navigate({
      pathname: location.pathname,
      search: urlSearchParams.toString()
    }, { replace: true });
  };

  const renderSingleDayRatio = (percentage, text) => {
    return (
      <Space direction="vertical">
        {
          percentage ? (
            <Progress type="circle" percent={percentage} width={80} format={(percent) => `${Math.floor(percent)}%`} />
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} className="mt-2 mb-0" />
          )
        }
        {text}
      </Space>
    );
  };

  const renderCurrentMonitoringStatus = (record) => {
    switch (record.currentStatus) {
      case MONITORING_STATUSES.UP:
        return (
          <span className="text-center">
            <Col>
              <CheckCircleIcon className="text-success fs-xl" />
            </Col>
            <Col>
              <Typography.Text type="success" strong>{t('Agent is working properly')}</Typography.Text>
            </Col>
          </span>
        );

      case MONITORING_STATUSES.SEEMS_DOWN:
      case MONITORING_STATUSES.DOWN:
        return (
          <span className="text-center">
            <Col>
              <CloseCircleIcon className="text-warning fs-xl" />
            </Col>
            <Col>
              <Typography.Text type="warning" strong>{t('Agent has errors')}</Typography.Text>
            </Col>
          </span>
        );

      default:
        return (
          <span className="text-center">
            <Col>
              <MinusOutlined className="text-secondary fs-xl" />
            </Col>
            <Col>
              <Typography.Text className="text-secondary" strong>{t('Agent has no uptime data')}</Typography.Text>
            </Col>
          </span>
        );
    }
  };

  const renderExpandedRow = (record) => {
    return (
      <Row gutter={24} className="px-5 py-4">
        <Col span={24}>
          <h3>{t('Overall Uptime')}</h3>
        </Col>
        <Col span={24}>
          <Space size="large">
            {renderSingleDayRatio(record.last1dRatio, t('Last 24 hours'))}
            {renderSingleDayRatio(record.last7dRatio, t('Last 7 days'))}
            {renderSingleDayRatio(record.last30dRatio, t('Last 30 days'))}
            {renderSingleDayRatio(record.last90dRatio, t('Last 90 days'))}
          </Space>
        </Col>
      </Row>
    );
  };

  const renderFiltersSection = () => {
    return (
      <Form
        name="monitoringTableFiltersForm"
        initialValues={{
          tenantsFilter: initialTenantFilters,
          endDate: observatoryFilters.endDate
        }}
        form={filtersForm}
        requiredMark={false}
      >
        <Row gutter={[12, 12]} className="mb-4">
          <div className="w-100">
            <ObservatoryFiltersRow
              form={filtersForm}
              rangePickerProps={{
                disabled: isFetchingMonitoring,
                timezone: orgTimezone,
                type: 'week'
              }}
              agentsPickerProps={{
                agentsList: bots?.items || [],
                disabled: isFetchingMonitoring,
                loading: isFetchingBots
              }}
            />
          </div>
        </Row>
      </Form>
    );
  };

  return (monitoring && monitoring.items) || isFetchingMonitoring || fetchMonitoringError?.status === 429 ? (
    <Fragment>
      {renderFiltersSection()}

      <Row gutter={24}>
        <Col xs={24}>
          <Table
            rowKey={record => record.tenant?.id}
            size="small"
            columns={columns}
            pagination={{
              size: 'default',
              total: monitoring?.total || 0,
              defaultCurrent: 1,
              current: currentPage,
              pageSize: pageSize,
              showSizeChanger: true,
              showTotal: (total) => `${t('Total Agents Monitored')}: ${total}`
            }}
            expandable={{
              columnWidth: 0,
              expandIcon: ({ expanded, onExpand, record }) =>
                expanded ? (
                  <MinusCircleOutlined onClick={e => onExpand(record, e)} className="ml-2" />
                ) : (
                  <PlusCircleOutlined onClick={e => onExpand(record, e)} className="ml-2" />
                ),
              expandedRowRender: record => renderExpandedRow(record)
            }}
            dataSource={monitoring?.items}
            loading={isFetchingMonitoring}
          />
        </Col>
      </Row>
    </Fragment>
  ) : fetchMonitoringError ? t('No monitoring is enabled in your organisation') : null;
};

export default MonitoringTable;
